import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../../services/apiClient.js";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl.js";
import footerData from "../../data/footerOriginalV1.json";
import {
  hasFooterContent,
  normalizePublicFooterPayload,
} from "../../utils/publicFooter.js";

const SOCIAL_ICONS = {
  instagram: "bi-instagram",
  facebook: "bi-facebook",
  x: "bi-twitter-x",
  youtube: "bi-youtube",
  linkedin: "bi-linkedin",
};

const LEGAL_COMPANY_NAME = "Northern Health Tech Pvt Ltd";

const isExternalLink = (url = "") =>
  /^https?:\/\//i.test(url) || url.startsWith("mailto:") || url.startsWith("tel:");

const scrollToPageTop = () => {
  const reset = () => {
    window.scrollTo(0, 0);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    [
      document.scrollingElement,
      document.documentElement,
      document.body,
      document.getElementById("root"),
    ]
      .filter(Boolean)
      .forEach((target) => {
        target.scrollTop = 0;
        target.scrollLeft = 0;
      });
  };
  reset();
  window.requestAnimationFrame(reset);
  [0, 80, 180, 350, 700, 1200, 2200].forEach((delay) => {
    window.setTimeout(reset, delay);
  });
};

const renderLink = (url, label, className) => {
  if (!url) return <span className={className}>{label}</span>;
  if (isExternalLink(url)) {
    return (
      <a href={url} className={className} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
  }
  return (
    <Link to={url} className={className} onClick={scrollToPageTop}>
      {label}
    </Link>
  );
};

function PartnerLogo({ item, index }) {
  const [imageFailed, setImageFailed] = useState(false);
  const fallbackLabel = item?.name || item?.label || item?.alt_text || `Partner ${index + 1}`;

  if (!item?.logo_url || imageFailed) {
    return (
      <div className="flex h-full w-full items-center justify-center px-4 py-3 text-center">
        <span className="text-sm font-semibold tracking-[0.08em] text-slate-700">
          {fallbackLabel}
        </span>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full items-center justify-center px-5 py-4">
      <img
        src={resolveMediaUrl(item.logo_url)}
        alt={item.alt_text || fallbackLabel}
        className="max-h-[36px] w-auto max-w-full object-contain"
        loading="lazy"
        onError={() => setImageFailed(true)}
      />
    </div>
  );
}

function FaqCard({
  eyebrow = "FAQ",
  title = "Frequently asked questions",
  description = "Quick answers to help you book confidently.",
  searchPlaceholder = "Search questions",
  items = [],
}) {
  const [query, setQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(-1);

  const filteredItems = useMemo(() => {
    const normalized = (Array.isArray(items) ? items : []).filter(
      (item) => item?.question && item?.answer
    );
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) return normalized;
    return normalized.filter((item) => {
      const question = String(item.question || "").toLowerCase();
      const answer = String(item.answer || "").toLowerCase();
      return question.includes(trimmed) || answer.includes(trimmed);
    });
  }, [items, query]);

  return (
    <div className="flex h-full flex-col rounded-3xl border border-white/15 bg-white/[0.04] p-6 md:p-8">
      <p className="text-xs uppercase tracking-[0.3em] text-[rgba(201,161,91,0.82)]">{eyebrow}</p>
      <h3 className="mt-3 text-2xl font-homevilas font-semibold text-white">{title}</h3>
      <p className="mt-2 text-sm text-white/70">{description}</p>
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={searchPlaceholder}
        className="mt-5 w-full rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/55"
      />
      <div className="mt-4 max-h-[220px] space-y-2 overflow-y-auto pr-1">
        {filteredItems.length ? (
          filteredItems.map((item, index) => {
            const isOpen = index === openIndex;
            return (
              <div key={item.id || `${item.question}-${index}`} className="rounded-2xl border border-white/10 bg-white/[0.03]">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-white"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                >
                  <span>{item.question}</span>
                  <span className="text-lg leading-none text-white/70">{isOpen ? "-" : "+"}</span>
                </button>
                {isOpen ? (
                  <div className="px-4 pb-4 text-sm leading-6 text-white/75">{item.answer}</div>
                ) : null}
              </div>
            );
          })
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-white/70">
            No matching FAQs.
          </div>
        )}
      </div>
    </div>
  );
}

export default function LandingFullFooter() {
  const [remoteFooter, setRemoteFooter] = useState(null);
  const [heroEmail, setHeroEmail] = useState("");
  const [heroStatus, setHeroStatus] = useState("");
  const [heroSubmitting, setHeroSubmitting] = useState(false);
  const [footerEmail, setFooterEmail] = useState("");
  const [footerStatus, setFooterStatus] = useState("");
  const [footerSubmitting, setFooterSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await apiGet("/api/public/footer");
        if (!active) return;
        setRemoteFooter(normalizePublicFooterPayload(response));
      } catch (_) {
        if (active) setRemoteFooter(null);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const footer = useMemo(() => {
    const fallback = normalizePublicFooterPayload(footerData);
    if (!hasFooterContent(remoteFooter)) return fallback;
    return {
      ...fallback,
      ...remoteFooter,
      brand_name: remoteFooter.brand_name || fallback.brand_name,
      brand_tagline: remoteFooter.brand_tagline || fallback.brand_tagline,
      about_text: remoteFooter.about_text || fallback.about_text,
      contact_email: remoteFooter.contact_email || fallback.contact_email,
      contact_phone: remoteFooter.contact_phone || fallback.contact_phone,
      contact_address: remoteFooter.contact_address || fallback.contact_address,
      newsletter_eyebrow: remoteFooter.newsletter_eyebrow || fallback.newsletter_eyebrow,
      newsletter_title: remoteFooter.newsletter_title || fallback.newsletter_title,
      newsletter_description: remoteFooter.newsletter_description || fallback.newsletter_description,
      newsletter_placeholder: remoteFooter.newsletter_placeholder || fallback.newsletter_placeholder,
      newsletter_button_label: remoteFooter.newsletter_button_label || fallback.newsletter_button_label,
      newsletter_compact_title:
        remoteFooter.newsletter_compact_title || fallback.newsletter_compact_title,
      newsletter_compact_description:
        remoteFooter.newsletter_compact_description || fallback.newsletter_compact_description,
      newsletter_compact_placeholder:
        remoteFooter.newsletter_compact_placeholder || fallback.newsletter_compact_placeholder,
      newsletter_compact_button_label:
        remoteFooter.newsletter_compact_button_label || fallback.newsletter_compact_button_label,
      cta_eyebrow: remoteFooter.cta_eyebrow || fallback.cta_eyebrow,
      cta_title: remoteFooter.cta_title || fallback.cta_title,
      cta_description: remoteFooter.cta_description || fallback.cta_description,
      cta_button_label: remoteFooter.cta_button_label || fallback.cta_button_label,
      cta_button_url: remoteFooter.cta_button_url || fallback.cta_button_url,
      faq_eyebrow: remoteFooter.faq_eyebrow || fallback.faq_eyebrow,
      faq_title: remoteFooter.faq_title || fallback.faq_title,
      faq_description: remoteFooter.faq_description || fallback.faq_description,
      faq_search_placeholder:
        remoteFooter.faq_search_placeholder || fallback.faq_search_placeholder,
      copyright_text: remoteFooter.copyright_text || fallback.copyright_text,
      company_links: remoteFooter.company_links?.length ? remoteFooter.company_links : fallback.company_links,
      support_links: remoteFooter.support_links?.length ? remoteFooter.support_links : fallback.support_links,
      places_links: remoteFooter.places_links?.length ? remoteFooter.places_links : fallback.places_links,
      faq_items: remoteFooter.faq_items?.length ? remoteFooter.faq_items : fallback.faq_items,
      partner_items: remoteFooter.partner_items?.length ? remoteFooter.partner_items : fallback.partner_items,
      social_links: remoteFooter.social_links?.length ? remoteFooter.social_links : fallback.social_links,
    };
  }, [remoteFooter]);

  const submitNewsletter = async ({
    email,
    setEmail,
    setStatus,
    setSubmitting,
    source,
  }) => {
    const trimmed = String(email || "").trim();
    if (!trimmed || !trimmed.includes("@")) {
      setStatus("Enter a valid email.");
      return;
    }
    setSubmitting(true);
    setStatus("");
    try {
      await apiPost("/api/public/newsletter/subscribe", { email: trimmed, source });
      setEmail("");
      setStatus("Thanks for subscribing.");
    } catch (err) {
      setStatus(err?.message || "Subscription failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasPartnersCopy = Boolean(
    footer.partners_eyebrow || footer.partners_title || footer.partners_description
  );
  const showPartnersSection =
    footer.partners_enabled && Array.isArray(footer.partner_items) && footer.partner_items.length > 0;

  return (
    <footer className="bg-[var(--footer-dark)] text-slate-200">
      <div className="relative overflow-hidden border-b border-white/8 bg-[radial-gradient(circle_at_top_left,rgba(201,161,91,0.16),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(15,92,90,0.18),transparent_42%),linear-gradient(180deg,rgba(7,20,36,0.97)_0%,rgba(7,20,36,1)_100%)]">
        <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.9fr)_minmax(320px,1fr)]">
            <div className="flex h-full flex-col rounded-3xl border border-white/15 bg-white/[0.04] p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.35em] text-[rgba(201,161,91,0.82)]">
                {footer.newsletter_eyebrow}
              </p>
              <h3 className="mt-3 text-3xl font-homevilas font-semibold text-white md:text-4xl">
                {footer.newsletter_title}
              </h3>
              <p className="mt-3 text-sm text-white/80 md:text-base">
                {footer.newsletter_description}
              </p>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  submitNewsletter({
                    email: heroEmail,
                    setEmail: setHeroEmail,
                    setStatus: setHeroStatus,
                    setSubmitting: setHeroSubmitting,
                    source: "footer-hero",
                  });
                }}
                className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center"
              >
                <input
                  type="email"
                  value={heroEmail}
                  onChange={(event) => setHeroEmail(event.target.value)}
                  placeholder={footer.newsletter_placeholder}
                  className="w-full flex-1 rounded-full border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/55"
                  required
                />
                <button
                  type="submit"
                  disabled={heroSubmitting}
                  className="rounded-full bg-[var(--cta-dark)] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[var(--cta-dark-hover)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {heroSubmitting ? "Sending..." : footer.newsletter_button_label}
                </button>
              </form>
              {heroStatus ? <p className="mt-3 text-xs text-white/70">{heroStatus}</p> : null}
            </div>

            <div id="custom-tour" className="flex h-full flex-col rounded-3xl border border-white/15 bg-slate-950/50 p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.3em] text-[rgba(201,161,91,0.82)]">
                {footer.cta_eyebrow}
              </p>
              <h3 className="mt-3 text-2xl font-homevilas font-semibold text-white">
                {footer.cta_title}
              </h3>
              <p className="mt-2 text-sm text-white/70">{footer.cta_description}</p>
              <div className="mt-6 lg:mt-auto">
                {renderLink(
                  footer.cta_button_url,
                  footer.cta_button_label,
                  "inline-flex items-center justify-center rounded-full bg-[var(--cta-dark)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[var(--cta-dark-hover)]"
                )}
              </div>
            </div>

            <FaqCard
              eyebrow={footer.faq_eyebrow}
              title={footer.faq_title}
              description={footer.faq_description}
              searchPlaceholder={footer.faq_search_placeholder}
              items={footer.faq_items}
            />
          </div>
        </div>
      </div>

      {showPartnersSection ? (
        <div className="border-b border-white/8 bg-[linear-gradient(180deg,rgba(10,22,39,1)_0%,rgba(10,22,39,0.94)_100%)]">
          <div className="mx-auto max-w-6xl px-4 py-8 md:py-10">
            <div className="rounded-[2rem] border border-white/8 bg-[linear-gradient(135deg,rgba(247,242,233,0.98),rgba(239,234,224,0.95))] p-6 shadow-[0_30px_70px_rgba(4,12,24,0.35)] md:p-8">
              <div
                className={
                  hasPartnersCopy
                    ? "grid gap-6 lg:grid-cols-[minmax(220px,0.72fr)_minmax(0,1fr)] lg:items-center"
                    : ""
                }
              >
                {hasPartnersCopy ? (
                  <div>
                    {footer.partners_eyebrow ? (
                      <p className="text-xs uppercase tracking-[0.32em] text-slate-500">
                        {footer.partners_eyebrow}
                      </p>
                    ) : null}
                    {footer.partners_title ? (
                      <h3 className="mt-2 font-homevilas text-4xl font-semibold text-slate-900">
                        {footer.partners_title}
                      </h3>
                    ) : null}
                    {footer.partners_description ? (
                      <p className="mt-3 max-w-sm text-sm leading-6 text-slate-600">
                        {footer.partners_description}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                <div
                  className={
                    hasPartnersCopy
                      ? "grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5"
                      : "flex flex-wrap items-center justify-center gap-4"
                  }
                >
                  {footer.partner_items.map((item, index) => {
                    const content = <PartnerLogo item={item} index={index} />;

                    if (item.url) {
                      return (
                        <a
                          key={`${item.name}-${item.url}-${index}`}
                          href={item.url}
                          target="_blank"
                          rel="noreferrer"
                          className="group flex min-h-[88px] min-w-[104px] overflow-hidden rounded-[1.5rem] border border-slate-200/90 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.08)] transition hover:-translate-y-0.5 hover:border-[rgba(15,92,90,0.28)] hover:shadow-[0_22px_44px_rgba(15,23,42,0.12)]"
                        >
                          {content}
                        </a>
                      );
                    }

                    return (
                      <div
                        key={`${item.name}-${index}`}
                        className="group flex min-h-[88px] min-w-[104px] overflow-hidden rounded-[1.5rem] border border-slate-200/90 bg-white shadow-[0_18px_40px_rgba(15,23,42,0.08)]"
                      >
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="mx-auto max-w-6xl px-4 pb-12 pt-12">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.75fr_0.75fr_0.95fr_1fr] lg:items-start">
          <div>
            <p className="text-sm font-semibold text-white">{footer.brand_name}</p>
            <p className="mt-3 max-w-xs text-sm leading-6 text-slate-400">
              {footer.about_text || footer.brand_tagline}
            </p>
            <div className="mt-4 space-y-1 text-sm text-slate-400">
              {footer.contact_email ? <p>{footer.contact_email}</p> : null}
              {footer.contact_phone ? <p>{footer.contact_phone}</p> : null}
              {footer.contact_address ? <p>{footer.contact_address}</p> : null}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-500">
              Company
            </p>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              {footer.company_links.map((link) => (
                <li key={`company-${link.label}-${link.url}`}>
                  {renderLink(link.url, link.label, "transition hover:text-white")}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-500">
              Support
            </p>
            <ul className="mt-4 space-y-3 text-sm text-slate-300">
              {footer.support_links.map((link) => (
                <li key={`support-${link.label}-${link.url}`}>
                  {renderLink(link.url, link.label, "transition hover:text-white")}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.26em] text-slate-500">
              Places
            </p>
            <ul className="mt-4 grid grid-cols-2 gap-x-8 gap-y-3 text-sm text-slate-300">
              {footer.places_links.map((link) => (
                <li key={`place-${link.label}-${link.url}`}>
                  {renderLink(link.url, link.label, "transition hover:text-white")}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:justify-self-end lg:w-full lg:max-w-[248px]">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
              <p className="text-sm font-semibold text-white">{footer.newsletter_compact_title}</p>
              <p className="mt-2 text-sm leading-6 text-slate-400">
                {footer.newsletter_compact_description}
              </p>
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  submitNewsletter({
                    email: footerEmail,
                    setEmail: setFooterEmail,
                    setStatus: setFooterStatus,
                    setSubmitting: setFooterSubmitting,
                    source: "footer-card",
                  });
                }}
                className="mt-4 flex flex-col gap-3"
              >
                <input
                  type="email"
                  value={footerEmail}
                  onChange={(event) => setFooterEmail(event.target.value)}
                  placeholder={footer.newsletter_compact_placeholder}
                  className="w-full rounded-full border border-white/20 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/45"
                  required
                />
                <button
                  type="submit"
                  disabled={footerSubmitting}
                  className="rounded-full bg-[var(--cta-dark)] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[var(--cta-dark-hover)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {footerSubmitting ? "Sending..." : footer.newsletter_compact_button_label}
                </button>
              </form>
              {footerStatus ? <p className="mt-3 text-xs text-white/70">{footerStatus}</p> : null}
            </div>

            {footer.social_links.length ? (
              <div className="mt-5">
                <div className="flex flex-wrap items-center gap-3">
                  {footer.social_links.map((item) => (
                    <a
                      key={`${item.platform}-${item.url}`}
                      href={item.url}
                      aria-label={item.label || item.platform}
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-base text-white/80 transition hover:border-white/50 hover:text-white"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className={SOCIAL_ICONS[item.platform] || "bi-link-45deg"} />
                    </a>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-10 border-t border-white/10 pt-4 text-xs leading-6 text-slate-500">
          <div>{footer.copyright_text}</div>
          <div>Homevilas is operated by {LEGAL_COMPANY_NAME}.</div>
        </div>
      </div>
    </footer>
  );
}
